import React, { useState } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useAppSelector } from "../store/hooks";
import { submitNCGFeedback } from "../api/browseJournal";
import { notify } from "../utils/Notify";

const FEEDBACK_OPTIONS = [
  { label: "PLATFORM", value: "PLATFORM" },
  { label: "CONTENT", value: "CONTENT" },
];

interface FeedbackFormData {
  clientName: string;
  userName: string;
  department: string;
  //   userType: string;
  email: string;
  //   mobileNumber: string;
  libraryId: string;
  //   jgateId: string;
  subject: string;
  feedbackAbout: string[];
  message: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NCGFeedbackForm() {
  const loggedInCustomer = useAppSelector(
    (state: any) => state.customer?.customerDetails,
  );

  const loggedInUser = useAppSelector(
    (state: any) => state?.customer?.personalDetails,
  );

  const userId = sessionStorage.getItem("user_id");

  const userName = [loggedInUser?.firstname, loggedInUser?.lastname]
    .filter(Boolean)
    .map((name) => name.trim())
    .join(" ");

  const [formData, setFormData] = useState<FeedbackFormData>({
    clientName: loggedInCustomer?.displayname || "",
    userName: userName || "",
    department: loggedInUser?.department || "",
    // userType: loggedInUser?.profile_type || "",
    email: loggedInUser?.emailid || "",
    // mobileNumber: loggedInUser?.mobile || "",
    libraryId: loggedInUser?.libraryidnumber || "",
    // jgateId: userId || "",
    subject: "",
    feedbackAbout: [],
    message: "",
  });

  const [emailError, setEmailError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "email") {
      setEmailError(
        value && !EMAIL_REGEX.test(value) ? "Enter a valid email address" : "",
      );
    }
  };

  const handleFeedbackChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      feedbackAbout: prev.feedbackAbout.includes(value)
        ? prev.feedbackAbout.filter((item) => item !== value)
        : [...prev.feedbackAbout, value],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.userName || !formData.email) {
      notify("warning", "Username and Email are mandatory");
      return;
    }

    if (!EMAIL_REGEX.test(formData.email)) {
      setEmailError("Enter a valid email address");
      notify("warning", "Please enter a valid email address");
      return;
    }

    const payload = {
      ...formData,
      feedbackAbout: formData.feedbackAbout,
      customer_id: loggedInCustomer?.informaticscustomer_id || null,
      consortiamaster_id: loggedInCustomer?.consortiamaster_id || null,
    };

    try {
      const data: any = await submitNCGFeedback(payload);

      if (data?.message === "Ok") {
        notify("success", data?.data || "Feedback submitted successfully");
        setFormData((prev: any) => ({
          ...prev,
          subject: "",
          feedbackAbout: [],
          message: "",
        }));
      } else {
        notify("error", data?.data || "Submission failed");
      }
    } catch (error: any) {
      notify("error", error?.response?.data?.data || "Submission failed");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f5f5",
        px: 2,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 500,
          bgcolor: "white",
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Typography variant="h6" mb={3} textAlign="center">
          NCG Feedback Form
        </Typography>

        <Grid container spacing={2}>
          {[
            { label: "Name of Institution", name: "clientName" },
            { label: "Name of User", name: "userName", required: true },
            { label: "Department", name: "department" },
            { label: "Email", name: "email", required: true },
            // { label: "Library ID", name: "libraryId" },
            { label: "Subject", name: "subject", required: true },
          ].map((field) => (
            <Grid item xs={12} key={field.name}>
              <TextField
                fullWidth
                label={field.label}
                name={field.name}
                value={(formData as any)[field.name]}
                onChange={handleChange}
                required={field.required}
                error={field.name === "email" && !!emailError}
                helperText={field.name === "email" ? emailError : undefined}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Typography variant="subtitle1" mb={1}>
              Feedback About
            </Typography>
            {FEEDBACK_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={formData.feedbackAbout.includes(option.value)}
                    onChange={() => handleFeedbackChange(option.value)}
                  />
                }
                label={option.label}
              />
            ))}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              helperText={!formData.message.trim() ? "Message is required" : ""}
            />
          </Grid>

          <Grid item xs={12} textAlign="center">
            <Button type="submit" variant="contained" fullWidth>
              Submit Feedback
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
